import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccount } from '@ibk/shared-auth';
import { useContaDigital } from '@ibk/shared-config';
import { useTranslation } from '@ibk/shared-translation';
import { formatCurrency } from '@ibk/shared-utils';
import { useEmissorCreateOrderPayment, useEmissorOrder } from '../../../../../hooks';
import { EmissorOrderPaymentMethod, EmissorOrderPaymentStatus, } from '../../../../../services/contractOrderPaymentApi';
import { getRecommendedEmissorPaymentMethod } from '../getRecommendedEmissorPaymentMethod';
import { EmissorPaymentMethod } from '../types';
const BANK_SLIP_POLL_INTERVAL_MS = 5000;
const BANK_SLIP_POLL_TIMEOUT_MS = 60000;
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
function toOrderPaymentMethod(method) {
    return method === EmissorPaymentMethod.BANK_SLIP ? EmissorOrderPaymentMethod.BANKSLIP : EmissorOrderPaymentMethod.PIX;
}
function fromOrderPaymentMethod(method) {
    return method === EmissorOrderPaymentMethod.BANKSLIP ? EmissorPaymentMethod.BANK_SLIP : EmissorPaymentMethod.PIX;
}
export function useEmissorPaymentContent({ orderId, contractId, orderTotalValue, existingPaymentMethod = null, }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [hasSufficientBalance, setHasSufficientBalance] = useState(false);
    const [showGeneratingCharge, setShowGeneratingCharge] = useState(false);
    const [isGeneratingBankSlip, setIsGeneratingBankSlip] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);
    const { hasActivePJAccount } = useAccount();
    const { data: contaDigitalData, isLoading: isLoadingBalance } = useContaDigital();
    const { createOrderPayment, isLoading: isCreatingPayment } = useEmissorCreateOrderPayment();
    const { getOrder } = useEmissorOrder();
    const balance = useMemo(() => {
        const amount = contaDigitalData?.balance?.amount;
        return typeof amount === 'number' ? amount : 0;
    }, [contaDigitalData]);
    const predefinedMethod = existingPaymentMethod ? fromOrderPaymentMethod(existingPaymentMethod) : null;
    const isBankSlipPredefined = predefinedMethod === EmissorPaymentMethod.BANK_SLIP;
    const isPixPredefined = predefinedMethod === EmissorPaymentMethod.PIX;
    const isAccountActive = Boolean(hasActivePJAccount());
    const showAccountBalance = isAccountActive && !isBankSlipPredefined;
    const showPix = !isBankSlipPredefined;
    const showBankSlip = !isPixPredefined;
    const recommendedMethod = useMemo(() => getRecommendedEmissorPaymentMethod({
        showAccountBalance,
        hasSufficientBalance,
        showPix,
        showBankSlip,
    }), [showAccountBalance, hasSufficientBalance, showPix, showBankSlip]);
    useEffect(() => {
        setHasSufficientBalance(isAccountActive && balance >= orderTotalValue);
    }, [balance, isAccountActive, orderTotalValue]);
    useEffect(() => {
        if (isBankSlipPredefined) {
            setSelectedMethod(EmissorPaymentMethod.BANK_SLIP);
        }
        else if (showAccountBalance && hasSufficientBalance) {
            setSelectedMethod(EmissorPaymentMethod.ACCOUNT_BALANCE);
        }
        else {
            setSelectedMethod(EmissorPaymentMethod.PIX);
        }
    }, [showAccountBalance, hasSufficientBalance, isBankSlipPredefined]);
    const breadcrumbs = useMemo(() => [
        { label: t('recharge.breadcrumb.benefits'), href: '/beneficios' },
        { label: t('recharge.breadcrumb.new-recharge'), isActive: true },
    ], [t]);
    const currentStepConfig = useMemo(() => ({ id: 'payment', title: t('payment.step.title'), number: 5, status: 'active' }), [t]);
    const isLoading = isLoadingBalance || isCreatingPayment || isRedirecting || isGeneratingBankSlip;
    const handleMethodSelect = (method) => {
        setSelectedMethod(method);
    };
    const showCannotProceedAlert = () => {
        setAlertMessage(t('payment.error.cannot-proceed'));
        setShowAlert(true);
    };
    const navigateToBankSlip = () => {
        setIsRedirecting(true);
        navigate(`/beneficios/recarga/${orderId}/pagar/boleto`);
    };
    const pollBankSlipOrder = async () => {
        const deadline = Date.now() + BANK_SLIP_POLL_TIMEOUT_MS;
        while (Date.now() < deadline) {
            await delay(BANK_SLIP_POLL_INTERVAL_MS);
            try {
                const { payment } = await getOrder({ contractId, orderId });
                if (payment &&
                    payment.paymentMethod === EmissorOrderPaymentMethod.BANKSLIP &&
                    payment.status === EmissorOrderPaymentStatus.CREATED &&
                    payment.readableCode) {
                    return true;
                }
            }
            catch {
            }
        }
        return false;
    };
    const handleBankSlipPayment = async (payment) => {
        if (payment.status === EmissorOrderPaymentStatus.CREATED && payment.readableCode) {
            navigateToBankSlip();
            return;
        }
        setIsGeneratingBankSlip(true);
        const isReady = await pollBankSlipOrder();
        setIsGeneratingBankSlip(false);
        if (isReady) {
            navigateToBankSlip();
            return;
        }
        setShowGeneratingCharge(true);
    };
    const handlePayment = async () => {
        if (!selectedMethod || !orderId || !contractId)
            return;
        try {
            const payment = await createOrderPayment({
                contractId,
                orderId,
                paymentMethod: toOrderPaymentMethod(selectedMethod),
            });
            if (selectedMethod === EmissorPaymentMethod.BANK_SLIP) {
                await handleBankSlipPayment(payment);
                return;
            }
            if (payment.status === EmissorOrderPaymentStatus.PENDING) {
                setShowGeneratingCharge(true);
                return;
            }
            if (selectedMethod === EmissorPaymentMethod.ACCOUNT_BALANCE) {
                if (payment.readableCode) {
                    setIsRedirecting(true);
                    navigate('/conta-pj/pagar/pix-copia-e-cola', { state: { code: payment.readableCode } });
                    return;
                }
                showCannotProceedAlert();
                return;
            }
            if (payment.readableCode) {
                setIsRedirecting(true);
                navigate(`/beneficios/recarga/${orderId}/pagar/pix`);
                return;
            }
            showCannotProceedAlert();
        }
        catch {
            showCannotProceedAlert();
        }
    };
    const handleCloseAlert = () => {
        setShowAlert(false);
        setAlertMessage('');
    };
    const handleGoToBenefits = () => {
        navigate('/beneficios');
    };
    const getAccountBalanceDescription = () => {
        if (isLoadingBalance)
            return t('payment.method.account.loading');
        return hasSufficientBalance
            ? formatCurrency(balance)
            : `${formatCurrency(balance)} - ${t('payment.method.account.insufficient')}`;
    };
    return {
        selectedMethod,
        showAlert,
        alertMessage,
        hasAccount: isAccountActive,
        hasSufficientBalance,
        showAccountBalance,
        showPix,
        showBankSlip,
        isLoading,
        isLoadingBalance,
        isRetrying: isCreatingPayment || isGeneratingBankSlip,
        showGeneratingCharge,
        breadcrumbs,
        currentStepConfig,
        accountBalanceDescription: getAccountBalanceDescription(),
        totalValue: formatCurrency(orderTotalValue),
        recommendedMethod,
        handleMethodSelect,
        handlePayment,
        handleCloseAlert,
        handleGoToBenefits,
    };
}
